import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Breadcrumbs from '../../../components/BreadCrumbs';
import SearchBar from '../../../components/searchBar/SearchBar';
import Dropdown from '../../../components/inputFields/DropDown';
import Table from '../../../components/tables/Table';
import TabView from '../../../components/TabView';
import CustomLoading from '../../../components/customLoading/CustomLoading';
import B2BLeadDetail from '../../../components/leads/b2b/B2BLeadDetail';
import B2BLogInteractionModal from '../../../components/leads/b2b/B2BLogInteractionModal';
import { getB2BSalespersonDashboard, updateB2BLead, getB2BSalespersonList, quickAddB2BLead, toggleB2BDealerStatus } from '../../../services/b2bLeads';
import { getUserId, getUserRole, isB2bSalesperson, getUserToken } from '../../../helpers/local_storage_helpers';
import { B2B_OUTCOME_MAP, B2B_PURPOSE_MAP } from '../../../constants/b2bWorkflow';
import { toast } from 'react-toastify';
import { BsWhatsapp, BsX } from 'react-icons/bs';
import { Eye, PhoneCall } from 'lucide-react';
import { BiPlus, BiRefresh } from 'react-icons/bi';
import DatePicker from '../../../components/inputFields/DatePicker';
import { searchProductWithAkaName } from '../../../services/search';
import MultiAsyncDropdown from '../../../components/inputFields/MultiAsyncDropDown';
import { formatDateMonth } from '../../../utils/FormatDateTime';
import axios from 'axios';
import { subcategoriesCRUDUrl } from '../../../constants/apis';
import { sanitizeMobile } from '../../../utils/phone';

const TAB_KEYS = ['', 'new', 'follow_up', 'quotation_sent', 'negotiation', 'sample_requested', 'npc', 'not_interested'];
const TAB_LABELS = {
    '': 'All',
    new: 'New',
    follow_up: 'Follow Up',
    quotation_sent: 'Quotation Sent',
    negotiation: 'Negotiation',
    sample_requested: 'Sample Requested',
    npc: 'NPC',
    not_interested: 'Not Interested',
};

const formatDateTime = (value) => value ? new Date(value).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '—';
const isOverdue = (value) => value && new Date(value).getTime() < Date.now();

const OutcomeBadge = ({ value }) => {
    const option = B2B_OUTCOME_MAP[value || 'new'] || B2B_OUTCOME_MAP.new;
    return <span className={`px-2 py-0.5 rounded-full border text-[10px] font-black whitespace-nowrap ${option.badgeClass}`}>{option.label}</span>;
};


const B2BQuickAddLeadModal = ({ isOpen, onClose, onSuccess, staffOptions, currentUserRole, currentUserId }) => {
    const [phone, setPhone] = useState('');
    const [subcategories, setSubcategories] = useState([]);
    const [salesPerson, setSalesPerson] = useState(currentUserId || '');
    const [visitingCard, setVisitingCard] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            setVisitingCard(e.dataTransfer.files[0]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const cleanPhone = phone.replace(/\D/g, '');
        if (!cleanPhone) {
            toast.error("Phone number is required");
            return;
        }
        if (cleanPhone.length !== 10) {
            toast.error("Please enter a valid 10-digit phone number");
            return;
        }

        setSubmitting(true);
        try {
            const formData = new FormData();
            formData.append('phone', cleanPhone);
            
            if (subcategories && subcategories.length > 0) {
                const subIds = subcategories.map(s => s.value).join(',');
                formData.append('product_subcategory', subIds);
            }

            if (visitingCard) {
                formData.append('visiting_card', visitingCard);
            }

            if (salesPerson) {
                formData.append('sales_person', salesPerson);
            }

            const res = await quickAddB2BLead(formData);
            if (res.status === 201 || res.status === 200) {
                toast.success("B2B Lead added successfully!");
                onSuccess();
                onClose();
            } else {
                toast.error(res.data?.detail || "Failed to add B2B lead");
            }
        } catch (error) {
            console.error("Error adding quick lead:", error);
            toast.error("An error occurred while adding the lead");
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    const canSelectUser = currentUserRole === 'admin' || currentUserRole === 'staff';

    return (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 text-left">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                <div className="px-6 py-4 bg-gradient-to-r from-emerald-600 to-teal-600 text-white flex justify-between items-center">
                    <h3 className="text-lg font-black tracking-tight">Quick Add B2B Lead</h3>
                    <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
                        <BsX size={24} />
                    </button>
                </div>
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Phone Number *</label>
                        <input
                            type="text"
                            required
                            placeholder="Enter 10-digit mobile number"
                            value={phone}
                            onChange={e => setPhone(sanitizeMobile(e.target.value))}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20 transition-all font-medium"
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Interested Sub-Categories</label>
                        <MultiAsyncDropdown
                            name="quick_add_subcategories"
                            placeholder="Search sub-categories..."
                            apiCallFunction={async (input) => {
                                const token = getUserToken();
                                const url = `${subcategoriesCRUDUrl}?page_size=50${input ? `&search=${input}` : ''}`;
                                const response = await axios.get(url, {
                                    headers: { Authorization: `Token ${token}` }
                                });
                                const raw = response?.data?.results ?? response?.data;
                                return (Array.isArray(raw) ? raw : []).map(item => ({
                                    value: item.id,
                                    label: item.name ?? "Subcategory",
                                }));
                            }}
                            value={subcategories}
                            onChange={setSubcategories}
                            needSearch
                        />
                    </div>

                    {canSelectUser && (
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Salesperson</label>
                            <Dropdown
                                options={staffOptions}
                                value={salesPerson}
                                onChange={setSalesPerson}
                                placeholder="Select Salesperson"
                                needSearch
                                compact
                            />
                        </div>
                    )}

                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Visiting Card</label>
                        <div
                            onDragEnter={handleDrag}
                            onDragOver={handleDrag}
                            onDragLeave={handleDrag}
                            onDrop={handleDrop}
                            className={`border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer relative ${
                                dragActive ? 'border-primary bg-primary/5' : 'border-gray-300 hover:border-gray-400 bg-gray-50'
                            }`}
                        >
                            <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                id="quick-add-card-file"
                                onChange={e => setVisitingCard(e.target.files[0] || null)}
                            />
                            <label htmlFor="quick-add-card-file" className="cursor-pointer space-y-2 block w-full">
                                <div className="flex justify-center">
                                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                </div>
                                {visitingCard ? (
                                    <div className="text-xs font-bold text-emerald-600 bg-emerald-50 py-1 px-2 rounded inline-block">
                                        Selected: {visitingCard.name}
                                    </div>
                                ) : (
                                    <div className="text-xs text-gray-500">
                                        <span className="font-bold text-primary">Click to upload</span> or drag & drop visiting card image
                                    </div>
                                )}
                            </label>
                        </div>
                    </div>

                    <div className="flex justify-end gap-2 pt-2 border-t mt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 text-gray-700 text-xs font-bold rounded-lg hover:bg-gray-50 transition-all uppercase tracking-wider"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="px-5 py-2 bg-primary text-white text-xs font-black rounded-lg hover:bg-primary/90 transition-all uppercase tracking-wider disabled:bg-gray-300 flex items-center gap-2"
                        >
                            {submitting ? 'Adding...' : 'Add Lead'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};


const B2BSalesPersonView = () => {
    const role = getUserRole();
    const canSelectUser = role === 'admin' || role === 'staff';
    const [loading, setLoading] = useState(false);
    const [dashboard, setDashboard] = useState({ stats: {}, tab_counts: {}, follow_up_queue: [], leads: [] });
    const [selectedUser, setSelectedUser] = useState(canSelectUser ? '' : getUserId());
    const [staffOptions, setStaffOptions] = useState([]);
    const [status, setStatus] = useState('');
    const [search, setSearch] = useState('');
    const [urgentOnly, setUrgentOnly] = useState(false);
    const [detailLead, setDetailLead] = useState(null);
    const [logModal, setLogModal] = useState({ open: false, lead: null, completeFollowUpId: null });
    const [viewingCardUrl, setViewingCardUrl] = useState(null);
    const [showQuickAddModal, setShowQuickAddModal] = useState(false);
    const [selectedDate, setSelectedDate] = useState('');

    const fetchDashboard = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (canSelectUser && selectedUser) params.set('user_id', selectedUser);
            if (status) params.set('status', status);
            if (search) params.set('search', search);
            if (urgentOnly) params.set('is_urgent', 'true');
            if (selectedDate) params.set('date', selectedDate);
            const res = await getB2BSalespersonDashboard(params.toString());
            if (res.status === 200) {
                setDashboard(res.data);
            } else {
                toast.error(res?.data?.detail || "Failed to fetch B2B workspace");
            }
        } catch (error) {
            toast.error("Error fetching B2B workspace");
        } finally {
            setLoading(false);
        }
    }, [canSelectUser, selectedUser, status, search, urgentOnly, selectedDate]);

    const handleVisitingCardUpload = async (leadId, file) => {
        if (!file) return;
        const toastId = toast.loading("Uploading visiting card...");
        try {
            const formData = new FormData();
            formData.append('visiting_card', file);
            const res = await updateB2BLead(leadId, formData);
            if (res.status === 200) {
                toast.update(toastId, { render: "Visiting card uploaded successfully", type: "success", isLoading: false, autoClose: 3000 });
                fetchDashboard();
            } else {
                toast.update(toastId, { render: "Failed to upload visiting card", type: "error", isLoading: false, autoClose: 3000 });
            }
        } catch (error) {
            console.error("Error uploading visiting card:", error);
            toast.update(toastId, { render: "Failed to upload visiting card", type: "error", isLoading: false, autoClose: 3000 });
        }
    };

    const handleToggleDealer = useCallback(async (item) => {
        const assignmentId = item.assigned_to?.assignment_id;
        if (!assignmentId) {
            toast.error("Assignment ID not found for this lead");
            return;
        }
        const currentStatus = item.assigned_to?.is_my_dealer || false;
        const newStatus = !currentStatus;
        
        try {
            const res = await toggleB2BDealerStatus(assignmentId, newStatus);
            if (res.status === 200 || res.status === 204) {
                toast.success(`Successfully ${newStatus ? 'marked as' : 'unmarked from'} My Dealers`);
                fetchDashboard();
            } else {
                toast.error(res.data?.detail || "Failed to update dealer status");
            }
        } catch (error) {
            toast.error("Error updating dealer status");
        }
    }, [fetchDashboard]);

    const fetchStaff = async () => {
        if (!canSelectUser) return;
        try {
            const res = await getB2BSalespersonList();
            if (res.status === 200) {
                const list = res.data.results || res.data || [];
                const options = list.map(user => ({
                    label: user.name || user.username || user.email,
                    value: user.id,
                }));
                setStaffOptions(options);
                if (options.length > 0 && !selectedUser) {
                    setSelectedUser(options[0].value);
                }
            }
        } catch (error) {
            console.error("Error fetching salespeople:", error);
        }
    };


    useEffect(() => {
        fetchStaff();
    }, []);

    useEffect(() => {
        fetchDashboard();
    }, [fetchDashboard]);    const stats = dashboard.stats || {};
    const leads = dashboard.leads || [];

    const headers = [
        { label: "Date", key: "created_at", width: "85px" },
        { label: "Shop/Owner", key: "shop_owner" },
        { label: "Phone", key: "phone", width: "110px" },
        { label: "Product Sub-Category", key: "product_subcategory", width: "130px" },
        { label: "Last Outcome", key: "last_outcome", width: "120px" },
        { label: "District/State", key: "location" },
        { label: "Last Order Date", key: "last_order_date", width: "110px" },
        { label: "Flags", key: "flags", width: "95px" },
        { label: "Actions", key: "actions", width: "140px" },
    ];

    const mapLeadToRow = useCallback((item) => ({
        created_at: (
            <span className="text-[10px] text-gray-500 font-medium">
                {formatDateMonth(item.created_at)}
            </span>
        ),
        shop_owner: (
            <button onClick={() => setDetailLead(item)} className="text-left w-full">
                <div className="text-xs font-black text-gray-800 hover:text-primary">{item.shop_name || 'Unnamed shop'}</div>
                <div className="text-[10px] text-gray-500">{item.owner_name || 'No owner'}</div>
            </button>
        ),
        phone: (
            <div className="flex flex-col gap-1 items-start">
                {item.phone ? (
                    <button onClick={() => setDetailLead(item)} className="text-sm font-bold text-primary hover:underline text-left">
                        {item.phone}
                    </button>
                ) : '—'}
                <div className="mt-0.5">
                    {item.visiting_card ? (
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => setViewingCardUrl(item.visiting_card)}
                                className="flex items-center gap-1 px-1.5 py-0.5 bg-gray-50 border border-gray-200 rounded text-[9px] font-bold text-gray-600 hover:bg-gray-100 transition-all whitespace-nowrap"
                            >
                                <Eye size={10} /> View Card
                            </button>
                            <label className="bg-white border rounded p-0.5 shadow-sm cursor-pointer hover:bg-gray-50 flex items-center justify-center">
                                <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => handleVisitingCardUpload(item.id, e.target.files[0])}
                                />
                                <svg className="w-2.5 h-2.5 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
                                </svg>
                            </label>
                        </div>
                    ) : (
                        <label className="flex items-center gap-1 px-1.5 py-0.5 bg-gray-50 border border-dashed border-gray-300 rounded text-[9px] font-bold text-gray-500 hover:bg-gray-100 hover:border-gray-400 cursor-pointer transition-all whitespace-nowrap">
                            <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => handleVisitingCardUpload(item.id, e.target.files[0])}
                            />
                            <span>+ Visiting Card</span>
                        </label>
                    )}
                </div>
            </div>
        ),
        product_subcategory: (
            <div className="flex flex-wrap gap-1 max-w-[150px]">
                {(item.product_subcategory || item.product_sub_category || item.subcategory || item.sub_category || []).map(sc => (
                    <span key={sc.id || sc} className="px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded-full text-[9px] font-bold">
                        {sc.name || sc}
                    </span>
                ))}
                {!(item.product_subcategory || item.product_sub_category || item.subcategory || item.sub_category || []).length && (
                    <span className="text-[10px] text-gray-400">—</span>
                )}
            </div>
        ),
        last_outcome: (
            <div className="flex flex-col gap-1">
                <div>
                    <OutcomeBadge value={item.last_outcome || 'new'} />
                    {item.last_outcome_comment && (
                        <div className="text-[10px] text-gray-500 italic max-w-[120px] truncate" title={item.last_outcome_comment}>
                            {item.last_outcome_comment}
                        </div>
                    )}
                </div>
                {item.next_follow_up?.follow_up_at && (
                    <div className={`text-[10px] font-bold ${isOverdue(item.next_follow_up.follow_up_at) ? 'text-rose-600' : 'text-gray-500'}`} title="Next Follow-up">
                        📅 {new Date(item.next_follow_up.follow_up_at).toLocaleDateString([], { dateStyle: 'medium' })}
                    </div>
                )}
            </div>
        ),
        location: (
            <div className="text-xs text-gray-600">
                <div>{item.district_name || item.district || '—'} / {item.state_name || item.state || '—'}</div>
                {item.area && <div className="text-[10px] text-gray-400">{item.area}</div>}
            </div>
        ),
        last_order_date: (
            <span className="text-[10px] font-medium text-gray-600">
                {item.last_order_date || '—'}
            </span>
        ),
        flags: (
            <div className="flex gap-1.5 flex-wrap items-center">
                {item.is_paid && <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-black">₹</span>}
                {item.is_urgent && <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-black">⚡</span>}
                <button
                    onClick={() => handleToggleDealer(item)}
                    className="p-1 hover:bg-gray-100 rounded-full transition-all"
                    title={item.assigned_to?.is_my_dealer ? "Unmark as My Dealer" : "Mark as My Dealer"}
                >
                    <span className={`text-sm ${item.assigned_to?.is_my_dealer ? "text-amber-500 animate-pulse font-black" : "text-gray-300 hover:text-amber-400"}`}>
                        ★
                    </span>
                </button>
            </div>
        ),
        actions: (
            <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                <button onClick={() => setLogModal({ open: true, lead: item, completeFollowUpId: null })} className="px-2 py-1.5 bg-primary text-white rounded-lg text-[10px] font-black flex items-center gap-1"><PhoneCall size={13} /> Log</button>
                <button onClick={() => setDetailLead(item)} className="p-1.5 bg-gray-100 text-gray-600 rounded-full"><Eye size={13} /></button>
            </div>
        ),
        _original: item,
    }), [handleVisitingCardUpload, handleToggleDealer]);

    const rows = useMemo(() => leads.map(mapLeadToRow), [leads, mapLeadToRow]);

    return (
        <div className="space-y-3">
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-100 shadow-sm">
                <Breadcrumbs items={[{ name: 'Home', link: '/dashboard' }, { name: 'Leads' }, { name: 'My B2B Leads' }]} />
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mt-2">
                    <div>
                        <h1 className="text-2xl font-black text-gray-800">My B2B Leads</h1>
                        <p className="text-xs font-bold text-gray-500">{dashboard.date || new Date().toLocaleDateString()}</p>
                    </div>
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-2">
                        <button
                            onClick={() => setShowQuickAddModal(true)}
                            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black rounded-lg transition-all shadow-sm flex items-center justify-center gap-1.5 whitespace-nowrap h-[34px]"
                        >
                            <BiPlus size={16} /> Quick Add Lead
                        </button>
                        {canSelectUser && (
                            <div className="w-full md:w-72">
                                <Dropdown options={staffOptions} value={selectedUser} onChange={setSelectedUser} placeholder="Viewing: salesperson" needSearch compact />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                    { label: "Today's Follow-ups", value: stats.todays_follow_ups || 0, className: 'text-primary' },
                    { label: 'Overdue', value: stats.overdue_follow_ups || 0, className: 'text-rose-600' },
                    { label: 'Calls Today', value: stats.calls_today || 0, className: 'text-blue-600' },
                ].map(stat => (
                    <div key={stat.label} className="bg-white rounded-xl border border-gray-100 shadow-sm p-3">
                        <div className="text-[10px] font-black text-gray-500 uppercase tracking-wider">{stat.label}</div>
                        <div className={`text-2xl font-black ${stat.className}`}>{stat.value}</div>
                    </div>
                ))}
            </div>

            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3">
                <div className="text-sm font-black text-gray-800 mb-3">Today's follow-up queue</div>
                <div className="flex gap-3 overflow-x-auto pb-1">
                    {(dashboard.follow_up_queue || []).map(item => (
                        <div key={item.id} className="min-w-[240px] border border-gray-100 rounded-xl p-3 bg-gray-50">
                            <div className="flex justify-between items-center">
                                <span className={`text-xs font-black ${item.is_overdue ? 'text-rose-600' : 'text-gray-700'}`}>{item.is_overdue ? 'OVERDUE · ' : ''}{formatDateTime(item.follow_up_at)}</span>
                                <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-black">{B2B_PURPOSE_MAP[item.purpose]?.label || item.purpose}</span>
                            </div>
                            <div className="mt-2 text-sm font-black text-gray-800">{item.shop_name}</div>
                            <div className="text-[11px] text-gray-500">{item.owner_name} · {item.district}</div>
                            <button onClick={() => setLogModal({ open: true, lead: { ...item, id: item.lead_id }, completeFollowUpId: item.id })} className="mt-3 px-3 py-1.5 bg-primary text-white rounded-lg text-[10px] font-black flex items-center gap-1">
                                <PhoneCall size={13} /> Log
                            </button>
                        </div>
                    ))}
                    {(dashboard.follow_up_queue || []).length === 0 && <div className="text-sm text-gray-400 italic py-6">No follow-ups waiting right now.</div>}
                </div>
            </div>

            <div className="flex flex-col md:flex-row gap-2 md:items-center justify-between">
                <div className="flex flex-col sm:flex-row gap-2 sm:items-center flex-1">
                    <SearchBar placeholder="Search B2B leads..." onSearch={setSearch} value={search} compact />
                    <div className="w-full sm:w-44">
                        <DatePicker
                            value={selectedDate}
                            onChange={setSelectedDate}
                            placeholder="Filter by Date"
                        />
                    </div>
                    {selectedDate && (
                        <button
                            onClick={() => setSelectedDate('')}
                            className="p-1.5 text-primary hover:bg-primary/10 rounded-full transition-colors flex items-center justify-center self-center"
                            title="Reset Date Filter"
                        >
                            <BiRefresh size={20} />
                        </button>
                    )}
                </div>
                <button onClick={() => setUrgentOnly(!urgentOnly)} className={`px-3 py-2 rounded-lg text-xs font-black border ${urgentOnly ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-white text-gray-500 border-gray-200'}`}>⚡ Urgent only</button>
            </div>

            <TabView
                onChanged={setStatus}
                isCenterAligned={false}
                tabs={TAB_KEYS.map(key => ({
                    key,
                    title: `${TAB_LABELS[key]} ${key ? `(${dashboard.tab_counts?.[key] || 0})` : `(${leads.length})`}`,
                    content: status === key ? (
                        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
                            {loading ? <div className="flex justify-center py-20"><CustomLoading size={60} /></div> : <Table headers={headers} rows={rows} />}
                        </div>
                    ) : null,
                }))}
            />



            {detailLead && <B2BLeadDetail lead={detailLead} onClose={() => setDetailLead(null)} onUpdated={fetchDashboard} />}
            <B2BLogInteractionModal
                isOpen={logModal.open}
                lead={logModal.lead}
                completeFollowUpId={logModal.completeFollowUpId}
                onClose={() => setLogModal({ open: false, lead: null, completeFollowUpId: null })}
                onSuccess={fetchDashboard}
            />

            <B2BQuickAddLeadModal
                isOpen={showQuickAddModal}
                onClose={() => setShowQuickAddModal(false)}
                onSuccess={fetchDashboard}
                staffOptions={staffOptions}
                currentUserRole={role}
                currentUserId={getUserId()}
            />

            {/* Visiting Card Lightbox Modal */}
            {viewingCardUrl && (
                <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4" onClick={() => setViewingCardUrl(null)}>
                    <div className="relative max-w-3xl max-h-[90vh] bg-white rounded-2xl p-2 shadow-2xl animate-in fade-in zoom-in-95 duration-150" onClick={e => e.stopPropagation()}>
                        <button
                            onClick={() => setViewingCardUrl(null)}
                            className="absolute -top-3 -right-3 bg-white border shadow-md text-gray-500 hover:text-gray-800 rounded-full p-1.5 transition-all active:scale-95 z-10"
                        >
                            <BsX size={20} />
                        </button>
                        <img
                            src={viewingCardUrl}
                            alt="Visiting Card Preview"
                            className="max-w-full max-h-[80vh] rounded-xl object-contain"
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default B2BSalesPersonView;
